<script setup lang="ts">
type ShopwareEntity = {
  isProduct: boolean
  isCategory: boolean
  foreignKey: string
}
const { apiClient } = useShopwareContext()
const { getCurrentShopLanguage, getLanguageByCode, changeShopLanguage } = useShopLanguages()
const { languageCode } = useLocale()
const route = useRoute()

const getShopwareEntity = async (): Promise<ShopwareEntity> => {
  if (!Array.isArray(route.params.slug)) {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
  }

  // required for browsser back behaviour
  const language = await getLanguageByCode(languageCode.value)
  if (language.id !== getCurrentShopLanguage()) await changeShopLanguage(languageCode.value)

  let slug = `${route.params.slug.join('/')}`
  const isTechnicalUrl = slug.startsWith('navigation/') || slug.startsWith('detail/')
  slug = isTechnicalUrl ? `/${slug}` : `shop/${slug}`

  const response = await apiClient.invoke('readSeoUrl post /seo-url', {
    body: {
      filter: [
        {
          type: 'multi',
          operator: 'or',
          queries: [
            {
              type: 'equals',
              field: isTechnicalUrl ? 'pathInfo' : 'seoPathInfo',
              value: slug,
            },
            {
              type: 'equals',
              field: isTechnicalUrl ? 'pathInfo' : 'seoPathInfo',
              value: slug.replace(/\/$/, ''),
            },
          ],
        },
      ],
    },
  })

  const seoUrlElement = response.data.elements[0]

  if (200 !== response.status || !seoUrlElement?.foreignKey) {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
  }

  return {
    isProduct: 'frontend.detail.page' === seoUrlElement.routeName,
    isCategory: 'frontend.navigation.page' === seoUrlElement.routeName,
    foreignKey: seoUrlElement.foreignKey,
  }
}

const seoUrl = await getShopwareEntity()

const { setShopware } = useLanguageSwitcherStore()

setShopware(seoUrl.foreignKey)
</script>
<template>
  <ShopCategoryPage v-if="seoUrl.isCategory" :foreign-key="seoUrl.foreignKey" />
  <ShopProductPage v-if="seoUrl.isProduct" :foreign-key="seoUrl.foreignKey" />
</template>
